// Shared event_id derivation for Meta's "Redundant Events" pattern.
//
// Both the client-side Pixel (fires on /webinar-thank-you) and the server-side
// CAPI function (functions/index.js, fires from the AC webhook) send a Lead
// event to Meta. With a MATCHING event_id, Meta deduplicates them into one
// conversion. Without it, both count — inflating lead numbers.
//
// The shared key both sides know is the EMAIL:
//   client  → from the form input at submit time
//   server  → from the AC webhook payload
//
// event_id formula (IDENTICAL on both sides, see functions/index.js):
//   webinar-lead-{sha256(email.trim().lower()).slice(0, 24)}
//
// IMPORTANT — two lessons baked in after Meta reported 0 % dedup coverage:
//
//   1. NO date/day suffix. An earlier version appended a UTC "day bucket"
//      (Math.floor(Date.now()/86400000)). The client computed it at thank-you
//      page load, the server computed it when AC's (delayed) webhook fired —
//      so they landed on different days and the ids never matched. The email
//      hash alone is stable and identical on both sides. If the same person
//      re-registers weeks later that's the same lead anyway → deduping is fine.
//
//   2. localStorage, NOT sessionStorage. The hash is captured on /webinar and
//      read on /webinar-thank-you. AC posts the form to *.activehosted.com and
//      redirects back — sessionStorage was lost across that cross-origin round
//      trip for ~50 % of users (mobile especially). localStorage is origin-
//      scoped and survives the round trip AND page reloads reliably.

const STORAGE_KEY = 'aa_webinar_lead_email_hash';
const FIRED_KEY = 'aa_webinar_lead_fired_id';
const MAX_AGE_MS = 60 * 60 * 1000; // 1 h — covers redirect + any reasonable delay

// SubtleCrypto-based SHA-256 → hex string.
export async function sha256Hex(input) {
  if (input == null) return null;
  const normalized = String(input).trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Deterministic event_id — email hash only, NO date component, so client and
// server always agree regardless of when each fires.
export function computeEventId(emailHashHex) {
  if (!emailHashHex) return null;
  return `webinar-lead-${emailHashHex.slice(0, 24)}`;
}

// Read the captured hash from localStorage. Returns null if capture never ran
// or the stored value is older than MAX_AGE_MS (stale from a previous session
// on a shared device).
export function readCapturedEmailHash() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.hash) return null;
    if (Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed.hash;
  } catch {
    return null;
  }
}

// Clear the captured hash. Called after a successful fire so a later unrelated
// visit to the thank-you page doesn't reuse this hash.
export function clearCapturedEmailHash() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

// Reload guard: has this exact event_id already been fired from this browser?
// Prevents a thank-you-page reload from firing a second (undeduped-against-
// itself) Lead. Returns true if already fired.
export function hasAlreadyFired(eventId) {
  if (!eventId || typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(FIRED_KEY) === eventId; } catch { return false; }
}

export function markFired(eventId) {
  if (!eventId || typeof localStorage === 'undefined') return;
  try { localStorage.setItem(FIRED_KEY, eventId); } catch { /* noop */ }
}

// Attach a submit listener to the AC form inside `root`. On submit, read the
// email input, hash it, and stash in localStorage with a timestamp. The
// thank-you page reads it back to derive the matching event_id.
export function attachWebinarLeadIdCapture(root) {
  if (!root || typeof window === 'undefined') return () => {};

  let hookedForm = null;
  let observer = null;
  let timeoutId = null;

  const findEmailInput = (form) =>
    form.querySelector('input[type="email"]') ||
    form.querySelector('input[name*="email" i]') ||
    form.querySelector('input[id*="email" i]');

  const onSubmit = async (event) => {
    // Do NOT preventDefault — AC's own submit logic must run normally.
    try {
      const form = event.currentTarget;
      const emailInput = findEmailInput(form);
      const email = emailInput && emailInput.value;
      if (!email) return;
      const hash = await sha256Hex(email);
      if (hash) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ hash, ts: Date.now() }));
      }
    } catch {
      // Never let a hashing error block the form. Worst case: no event_id
      // match → Meta falls back to fuzzy dedup on user_data.
    }
  };

  const tryHook = () => {
    if (hookedForm) return;
    const form = root.querySelector('form');
    if (!form) return;
    hookedForm = form;
    form.addEventListener('submit', onSubmit, { capture: true });
  };

  tryHook();
  if (hookedForm) return () => hookedForm.removeEventListener('submit', onSubmit, { capture: true });

  observer = new MutationObserver(() => {
    tryHook();
    if (hookedForm && observer) {
      observer.disconnect();
      observer = null;
    }
  });
  observer.observe(root, { childList: true, subtree: true });

  timeoutId = setTimeout(() => {
    if (observer) { observer.disconnect(); observer = null; }
  }, 15000);

  return () => {
    if (observer) observer.disconnect();
    if (timeoutId) clearTimeout(timeoutId);
    if (hookedForm) hookedForm.removeEventListener('submit', onSubmit, { capture: true });
  };
}
